import { useEffect } from 'react';

import { useZigzag } from '@common/device-manager';
import { useUBL } from '@common/log-manager';

import { logTTIMetrics, LogTTIMetricsParams } from './tti-core';

export interface TTIMetricsParams extends Omit<LogTTIMetricsParams, 'options' | 'sender'> {
  options?: {
    enabled?: boolean | (() => boolean);
    duration?: {
      min?: number;
      max?: number;
    };
  };
}

export const useTTIMetricsWebviewPage = (params: TTIMetricsParams) => {
  const { enabled = true } = params.options ?? {};
  const isWebview = useZigzag();

  useTTIMetricsPage({
    ...params,
    options: {
      ...params.options,
      enabled: () => {
        if (isWebview === null || isWebview === false) {
          return false;
        }

        return typeof enabled === 'function' ? enabled() : enabled;
      },
    },
  });
};

export const useTTIMetricsPage = (params: TTIMetricsParams) => {
  const { enabled = true } = params.options ?? {};
  const sendUBL = useUBL();

  useEffect(() => {
    if (enabled === false) {
      return;
    }

    if (typeof enabled === 'function' && enabled() === false) {
      return;
    }

    const sendTTIMetricsLog = () => {
      logTTIMetrics({
        ...params,
        sender: sendUBL,
      });
    };

    if (document.readyState === 'complete') {
      const timeout = setTimeout(sendTTIMetricsLog, 1000);
      const visibilityChangeListener = () => {
        if (document.visibilityState !== 'hidden') {
          return;
        }

        clearTimeout(timeout);
      };

      document.addEventListener('visibilitychange', visibilityChangeListener);
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('visibilitychange', visibilityChangeListener);
      };
    }

    const visibilityChangeListener = () => {
      if (document.visibilityState !== 'hidden') {
        return;
      }

      window.removeEventListener('load', sendTTIMetricsLog);
    };

    window.addEventListener('load', sendTTIMetricsLog);
    document.addEventListener('visibilitychange', visibilityChangeListener);

    return () => {
      window.removeEventListener('load', sendTTIMetricsLog);
      document.removeEventListener('visibilitychange', visibilityChangeListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};
